import React, { useMemo, useState } from 'react';
import { BsCaretDownFill, BsCaretRightFill, BsCalendarCheck, BsPersonCheck, BsClock, BsClockHistory, BsPersonX, BsExclamationCircle, BsCheckCircle } from "react-icons/bs";

const AppointmentTimelineKPI = ({ appointments = [], filteredAppointments = [], showKPIs = true }) => {
  const [showKPI, setShowKPI] = useState(showKPIs);

  // Ensure filteredAppointments is used if it exists, otherwise fall back to all appointments
  const effectiveAppointments = useMemo(() => {
    // If filteredAppointments is provided and different from all appointments, use it
    if (filteredAppointments && filteredAppointments.length > 0 && 
        filteredAppointments.length !== appointments.length) {
      return filteredAppointments;
    }
    // Otherwise use the full dataset
    return appointments;
  }, [appointments, filteredAppointments]);

  // Calculate all KPIs based on current effective appointments
  const kpis = useMemo(() => {
    // No data case
    if (!effectiveAppointments || effectiveAppointments.length === 0) {
      return {
        scheduledPatients: 0,
        attendedPatients: 0,
        waitingPatients: 0,
        appointmentCompleted: 0,
        noShows: 0,
        pendingPatients: 0,
        confirmedPatients: 0
      };
    }

    let scheduledPatients = 0;
    let attendedPatients = 0;
    let waitingPatients = 0;
    let appointmentCompleted = 0;
    let noShows = 0;
    let pendingPatients = 0;
    let confirmedPatients = 0;

    effectiveAppointments.forEach(appointment => {
      const status = appointment.status || '';
      const statusLower = status.toLowerCase();

      // Count scheduled patients (all appointments for the period)
      scheduledPatients++;

      // Count attended patients (in consultation)
      if (statusLower.includes('in consultation') || 
          statusLower.includes('attending') ||
          statusLower.includes('active')) {
        attendedPatients++;
      }

      // Count waiting patients
      if (statusLower.includes('waiting') || 
          statusLower.includes('called') ||
          statusLower.includes('arrived')) {
        waitingPatients++;
      }

      // Count completed appointments
      if (statusLower.includes('completed') || 
          statusLower.includes('finished') ||
          statusLower.includes('done')) {
        appointmentCompleted++;
      }

      // Count no-shows
      if (statusLower.includes('no-show') || 
          statusLower.includes('no show') ||
          statusLower.includes('absent') ||
          statusLower.includes('missed')) {
        noShows++;
      }

      // Count pending patients
      if (statusLower.includes('pending') || 
          statusLower.includes('scheduled') ||
          statusLower.includes('upcoming')) {
        pendingPatients++;
      }

      // Count confirmed patients
      if (statusLower.includes('confirmed') || 
          statusLower.includes('confirm')) {
        confirmedPatients++;
      }
    });

    return {
      scheduledPatients,
      attendedPatients,
      waitingPatients,
      appointmentCompleted,
      noShows,
      pendingPatients,
      confirmedPatients
    };
  }, [effectiveAppointments]);

  // Format number with commas for thousands
  const formatNumber = (value) => {
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return value;
  };

  // Toggle KPI visibility
  const toggleKPI = () => setShowKPI(prev => !prev);

  // Determine if filters are active
  const isFiltered = filteredAppointments && 
                    appointments && 
                    filteredAppointments.length !== appointments.length &&
                    filteredAppointments.length > 0;

  // Define the KPI cards data
  const kpiCards = [
    { 
      title: 'Appointments', 
      value: kpis.scheduledPatients,
      icon: <BsCalendarCheck />, 
      iconColor: '#f5f2e8'
    },
    { 
      title: 'Completed', 
      value: kpis.appointmentCompleted,
      icon: <BsClockHistory />, 
      iconColor: '#e8f4fd'
    },
    { 
      title: 'Waiting', 
      value: kpis.waitingPatients,
      icon: <BsClock />, 
      iconColor: '#fde8f4'
    },
    { 
      title: 'Pending', 
      value: kpis.pendingPatients,
      icon: <BsExclamationCircle />, 
      iconColor: '#e8f5e8'
    },
    { 
      title: 'Confirmed', 
      value: kpis.confirmedPatients,
      icon: <BsCheckCircle />, 
      iconColor: '#f0e8fd'
    },
    { 
      title: 'No-shows', 
      value: kpis.noShows,
      icon: <BsPersonX />, 
      iconColor: '#fff4e8'
    }
  ];

  return (
    <>
      {/* <div className="d-flex align-items-center justify-content-between mb-3 mt-4">
        <div className="d-flex align-items-center">
          <h5 className="mb-0 me-3">Dashboard / Appointment Timeline</h5>
          <button onClick={toggleKPI} className="btn text-primary p-0">
            {showKPI ? 'Hide KPI ' : 'Show KPI '}
            {showKPI ? <BsCaretDownFill size={12} /> : <BsCaretRightFill size={12} />}
          </button>
        </div>
        {isFiltered && (
          <div className="badge bg-info text-white">
            Showing filtered data ({filteredAppointments.length} of {appointments.length})
          </div>
        )}
      </div> */}
      
      {showKPI && (
        <div className="row gy-3 introbx_row">
          {kpiCards.map((kpi, index) => (
            <div key={index} className="col">
              <div className="d-flex align-items-center gap-2 p-3 rounded-10 shadow-sm">
                <div className="icon flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle" style={{ width: '2rem', height: '2rem', backgroundColor: '#E4E4FF', color: '#8381FF' }}>
                  <span className="d-block material-symbols-outlined fs-lg">{kpi.icon}</span>
                </div>
                <div className="info flex-fill">
                  <p className="fs-sm text-gray mb-0">{kpi.title}</p>
                  <p className="lh-1 fw-semibold">{formatNumber(kpi.value)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default AppointmentTimelineKPI;